import './lib/airtableHealth'; // fires health check fetch FIRST, before heavy imports
import React, { useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import ProgramManager from './components/ProgramManager';
import SessionReport from './components/reports/SessionReport';
import PasswordProtection from './components/PasswordProtection';
import AdminDashboard from './components/AdminDashboard';
import SalesDashboard from './components/SalesDashboard';
import PartnerPortal from './components/PartnerPortal';
import AirtableOutageBanner from './components/AirtableOutageBanner';
import { Toaster } from './components/ui/toaster';
import { toast } from './hooks/use-toast';
import { checkAndMigrateStorage, showStorageMigrationNotification } from './lib/storageMigration';

/**
 * MainLayout - Reusable layout component for main application pages
 * Provides consistent spacing and responsive design for program-related pages
 */
const MainLayout = ({ children }) => (
  <main className="min-h-screen bg-gray-50 py-4 px-4 sm:py-6 sm:px-6 lg:py-8">
    <div className="max-w-7xl mx-auto">
      {children}
    </div>
  </main>
);

const App = () => {
  const [migrationDone, setMigrationDone] = useState(false);
  
  // Handle storage migration on app startup
  useEffect(() => {
    // Run storage migration check immediately on app load
    const migrationPerformed = checkAndMigrateStorage();
    
    if (migrationPerformed) {
      // Show user-friendly notification about the migration
      const notification = showStorageMigrationNotification();
      
      // Small delay to ensure toast system is ready
      setTimeout(() => {
        toast({
          title: notification.title,
          description: notification.description,
          duration: 5000, // Show for 5 seconds
        });
      }, 1000);
    }
    
    // Mark migration as complete to allow components to read storage
    setMigrationDone(true);
  }, []); // Run only once on app mount
  
  // Don't render components that read localStorage until migration is done
  if (!migrationDone) {
    return (
      <>
        <AirtableOutageBanner />
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-gray-600">Loading app...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AirtableOutageBanner />
      <Routes>
        {/* Home route - with password protection */}
        <Route 
          path="/" 
          element={
            <PasswordProtection migrationDone={migrationDone}>
              <MainLayout><ProgramManager /></MainLayout>
            </PasswordProtection>
          } 
        />
        
        {/* Session report route - with password protection */}
        <Route 
          path="/sos/:soNumber/sessions/:sessionId/report" 
          element={
            <PasswordProtection migrationDone={migrationDone}>
              <MainLayout><SessionReport /></MainLayout>
            </PasswordProtection>
          } 
        />
        
        {/* Sales routes - separate authentication */}
        <Route 
          path="/sales/*" 
          element={<SalesDashboard />} 
        />
        
        {/* Partner Portal - public access via secure token (no authentication required) */}
        <Route 
          path="/p/:token" 
          element={<PartnerPortal />} 
        />
        
        {/* Admin routes - separate authentication, hidden in production via feature flag */}
        {(import.meta.env.VITE_ENABLE_ADMIN_FEATURES === 'true' || import.meta.env.DEV) && (
          <Route 
            path="/admin/*" 
            element={<AdminDashboard />} 
          />
        )}
        
        {/* Catch-all route for SO numbers - with password protection, must be last */}
        <Route 
          path="/:soNumber" 
          element={
            <PasswordProtection migrationDone={migrationDone}>
              <MainLayout><ProgramManager /></MainLayout>
            </PasswordProtection>
          } 
        />
      </Routes>
      <Toaster />
    </>
  );
};

export default App;